/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ImageList from '@mui/material/ImageList';
import {
  Box,
  Divider,
  Grid,
  SxProps,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { FormatListBulletedTwoTone, GridViewTwoTone } from '@mui/icons-material';
import { styled, Theme } from '@mui/material/styles';
import { Image } from './media-props';
import MediaListRow from './media/media-list-row';
import Thumbnail from './thumbnail';
import PhotoMediaViewer from '../../shared/material_components/photo-media-viewer';

type PhotoFieldItem = {
  photo_id: string,
  caption: string,
  showObjects: boolean,
  image?: Image,
};

export type PhotoMediaItem = {
  item: PhotoFieldItem,
}
export interface Props {
  images: Image[],
  record: Record<string, any>,
  editing?: boolean,
  disabled?: boolean,
  handleDeleteRequest?: (mediaID: string, fromMediaViewer?: boolean) => void,
  title?: string,
  sx?: SxProps<Theme>,
}

const GalleryDisplay: React.FC<Props> = ({
  images, record, editing = false, disabled = false, handleDeleteRequest = () => {}, title = '', sx = {},
}: Props) => {
  const [viewType, setViewType] = useState('list');
  const [openMedia, setOpenMedia] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedPhotoMedia, setSelectedPhotoMedia] = useState<PhotoMediaItem>(
    {
      item: {
        photo_id: '', caption: '', showObjects: false, image: undefined,
      },
    },
  );

  useEffect(() => {
    if (selectedPhotoMedia.item.photo_id.length) {
      setOpenMedia(true);
    }
  }, [selectedPhotoMedia.item.photo_id]);

  const cleanPhotoSelection = () => {
    setSelectedPhotoMedia({
      ...selectedPhotoMedia,
      item: {
        ...selectedPhotoMedia.item,
        photo_id: '',
      },
    });
  };

  const handleViewTypeChange = (
    event: React.MouseEvent<HTMLElement>, newViewType: string | null,
  ) => {
    setViewType(newViewType || viewType);
  };
  const allowDeleting = editing && !disabled;
  const FullDivider = styled(Divider)`
    margin-top: -5px;
    margin-left: -16px;
    width: calc(100% + 32px);
  `;

  const onMediaClose = () => {
    setOpenMedia(false);
    cleanPhotoSelection();
  };

  const onMediaViewerCaptionChange = (key: string|undefined, value: string|boolean) => {
    if (key === 'caption' && typeof (value) === 'string' && selectedPhotoMedia.item.image) {
      setHasChanges(true);
      setSelectedPhotoMedia({
        ...selectedPhotoMedia,
        item: {
          ...selectedPhotoMedia.item,
          caption: value,
        },
      });
    }
  };

  const onMediaViewerSave = () => {
    if (selectedPhotoMedia.item.image) {
      const { image, caption } = selectedPhotoMedia.item;
      image.onCaptionChange(caption);
    }
    cleanPhotoSelection();
    setOpenMedia(false);
    if (hasChanges) {
      toast('The caption has been updated successfully');
    }
  };

  const handleSelectMedia = (
    image: Image,
    newCaption?: string,
  ) => {
    setSelectedPhotoMedia({
      ...selectedPhotoMedia,
      item: {
        ...selectedPhotoMedia.item,
        photo_id: image.mediaID,
        caption: (newCaption || image.caption),
        image,
      },
    });
  };
  return (
    <Box>
      {openMedia && (
        <PhotoMediaViewer
          open={openMedia}
          record={record}
          mediaItem={selectedPhotoMedia}
          onMediaClose={onMediaClose}
          onMediaSave={onMediaViewerSave}
          onMediaChange={onMediaViewerCaptionChange}
          handleMediaDelete={(mediaId: string) => {
            handleDeleteRequest(mediaId, true);
            onMediaClose();
          }}
          hasChanges={hasChanges}
          resetHasChanges={() => setHasChanges(false)}
        />
      )}
      <Grid container spacing={2} sx={sx}>
        <Grid item xs={12} container justifyContent="flex-end">
          <Grid item xs={9}>
            <Typography
              variant="body1"
              noWrap
              sx={{
                lineHeight: '48px',
                mb: '0',
                fontWeight: '700',
                fontSize: '16px',
              }}
            >
              {title}
            </Typography>
          </Grid>
          <Grid item xs={3} container justifyContent="flex-end">
            <ToggleButtonGroup
              exclusive
              value={viewType}
              onChange={handleViewTypeChange}
              aria-label="View type"
            >
              <ToggleButton value="list" aria-label="list">
                <FormatListBulletedTwoTone />
              </ToggleButton>
              <ToggleButton value="thumbnails" aria-label="thumbnails">
                <GridViewTwoTone />
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <FullDivider />
        </Grid>
        <Grid item xs={12}>
          {(viewType === 'list' ? (
            <Grid container item spacing={2} xs={12}>
              {images.map((image) => (
                <Grid item xs={12} key={image.mediaID}>
                  <MediaListRow
                    url={image.thumbnailURL}
                    image={image}
                    mediaID={image.mediaID}
                    caption={image.caption}
                    altText={image.caption}
                    editable={editing}
                    disabled={disabled}
                    onDelete={handleDeleteRequest}
                    onCaptionChange={(newCaption: string) => image.onCaptionChange(newCaption)}
                    allowDeleting={allowDeleting}
                    selectMedia={handleSelectMedia}
                    sx={{
                      '& .MuiGrid-item': {
                        paddingTop: '0',
                      },
                      '&:hover': {
                        backgroundColor: '#EEE',
                      },
                      width: 'calc(100% + 32px)',
                      paddingRight: '16px',
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <ImageList sx={{ display: 'inline-grid', mt: '-8px' }} cols={4} gap={8}>
              {images.map((image) => (
                <Thumbnail
                  url={image.thumbnailURL}
                  image={image}
                  mediaID={image.mediaID}
                  allowDeleting={allowDeleting}
                  onDelete={image.onDelete}
                  handleDeleteRequest={handleDeleteRequest}
                  selectMedia={handleSelectMedia}
                  altText={image.caption}
                  key={image.mediaID}
                  imgStyles={{ borderRadius: '4px', width: '116px', height: '116px' }}
                  sx={{
                    '&:after': {
                      position: 'absolute',
                      content: '""',
                      display: 'none',
                      width: '100%',
                      height: '100%',
                      left: 0,
                      top: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
                      borderRadius: '4px',
                    },
                    '&:hover:after': {
                      display: 'block',
                    },
                    '& > .MuiImageListItemBar-root': {
                      zIndex: '1',
                    },
                  }}
                />
              ))}
            </ImageList>
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};

export default GalleryDisplay;
